import { useState } from "react";
import inksolverLogo from "../assets/inksolver-logo.png";

function GoogleIcon() {
  return (
    <svg className="login-google-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.92h5.38a4.6 4.6 0 0 1-2 3.02v2.54h3.24c1.9-1.75 2.98-4.33 2.98-7.41Z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.97-.9 6.62-2.36l-3.24-2.54c-.9.6-2.05.96-3.38.96-2.61 0-4.82-1.76-5.61-4.13H3.04v2.62A10 10 0 0 0 12 22Z" />
      <path fill="#FBBC05" d="M6.39 13.93A6 6 0 0 1 6.08 12c0-.67.11-1.32.31-1.93V7.45H3.04A10 10 0 0 0 2 12c0 1.63.39 3.17 1.04 4.55l3.35-2.62Z" />
      <path fill="#EA4335" d="M12 5.94c1.47 0 2.79.51 3.83 1.5l2.87-2.88A9.62 9.62 0 0 0 12 2a10 10 0 0 0-8.96 5.45l3.35 2.62C7.18 7.7 9.39 5.94 12 5.94Z" />
    </svg>
  );
}

function EyeIcon({ open }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {open ? (
        <>
          <path d="M2.5 12s3.3-6 9.5-6 9.5 6 9.5 6-3.3 6-9.5 6-9.5-6-9.5-6Z" />
          <circle cx="12" cy="12" r="2.5" />
        </>
      ) : (
        <>
          <path d="M3 3l18 18" />
          <path d="M10.6 6.1A10 10 0 0 1 12 6c6.2 0 9.5 6 9.5 6a14 14 0 0 1-2.3 3.1M6.2 6.2C3.7 8 2.5 12 2.5 12s3.3 6 9.5 6c1.6 0 3-.4 4.2-1" />
        </>
      )}
    </svg>
  );
}

function SlatePreview() {
  return (
    <div className="login-slate-preview" aria-label="A preview of the InkSolver slate with handwritten working">
      <div className="login-slate-heading">
        <div>
          <strong>Slate</strong>
          <span>handwritten solution and diagrams</span>
        </div>
        <div className="login-slate-tabs" aria-hidden="true">
          <span className="active">Slate</span>
          <span>Math Editor</span>
        </div>
      </div>

      <div className="login-slate-controls">
        <div className="login-slate-tutor">
          <span className="login-tutor-bulb" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M9 18h6M10 21h4M8.6 15.5A6 6 0 1 1 15.4 15.5c-.9.6-1.4 1.3-1.4 2.5h-4c0-1.2-.5-1.9-1.4-2.5Z" /></svg>
          </span>
          <span><strong>Tutor</strong><small>Guidance at every step</small></span>
        </div>

        <div className="login-slate-tools" aria-label="Slate tools">
          <span className="active" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="m4 16 11-11 4 4L8 20l-5 1 1-5ZM13 7l4 4" /></svg>
          </span>
          <span aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="m7 16 8-8 5 5-7 7H8l-4-4 4-4M13 20h8" /></svg>
          </span>
          <span aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M9 7 4 12l5 5M5 12h8a6 6 0 0 1 6 6" /></svg>
          </span>
          <span aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="M8 3H3v5M16 3h5v5M8 21H3v-5M16 21h5v-5" /></svg>
          </span>
        </div>
      </div>

      <div className="login-slate-canvas">
        <div className="login-slate-ink">
          <span className="line-one">S<sub>n</sub> = 9n + 2n<sup>2</sup></span>
          <span className="line-two">S<sub>1</sub> = a<sub>1</sub></span>
          <span className="line-three">a<sub>1</sub> = 9(1) + 2(1)<sup>2</sup> = 11</span>
          <span className="line-four">d = a<sub>2</sub> - a<sub>1</sub></span>
        </div>
        <svg className="login-slate-underline" viewBox="0 0 190 18" preserveAspectRatio="none" aria-hidden="true">
          <path d="M3 11c36-8 61 5 96-1 32-5 56 2 88-5" />
        </svg>
        <div className="login-slate-guidance">
          <span aria-hidden="true">i</span>
          <p><strong>Good setup.</strong> Find a<sub>2</sub> from S<sub>2</sub> - S<sub>1</sub>, then compare.</p>
        </div>
      </div>
    </div>
  );
}

export default function AuthScreen({
  mode,
  email,
  confirmEmail,
  password,
  loading,
  resetLoading,
  error,
  notice,
  allowSignup,
  privacyConsent,
  onModeChange,
  onEmailChange,
  onConfirmEmailChange,
  onPasswordChange,
  onPrivacyConsentChange,
  onOpenPrivacy,
  onSubmit,
  onGoogleLogin,
  onForgotPassword
}) {
  const [showPassword, setShowPassword] = useState(false);
  const isSignup = mode === "signup" && allowSignup;
  const title = isSignup ? "Create your account" : "Welcome back";
  const submitLabel = isSignup ? "Create account" : "Log in";

  return (
    <main className="login-page">
      <header className="login-topbar">
        <img src={inksolverLogo} alt="InkSolver" className="login-brand-logo" />
        <span className="login-brand-pill">Ink-driven math practice</span>
      </header>

      <section className="login-story" aria-labelledby="login-story-title">
        <div className="login-story-copy">
          <p className="login-eyebrow">AI THAT LETS YOU DO THE THINKING.</p>
          <h1 id="login-story-title">Write every step<br /><span>on the slate.</span></h1>
          <p className="login-story-principle">Because the 'how' matters more than the 'what'.</p>
          <p className="login-story-lede">
            Practice math in your own handwriting. Our AI tutor guides you at every step.
          </p>
        </div>

        <SlatePreview />
      </section>

      <section className="login-auth-zone" aria-label="Account access">
        <div className="login-auth-card">
          <div className="login-auth-heading">
            <span className="login-auth-kicker">YOUR INKSOLVER ACCOUNT</span>
            <h2>{title}</h2>
            <p>{isSignup ? "Start building a learning record that grows with you." : "Continue from your latest line of working."}</p>
          </div>

          <button
            className="login-google-button"
            type="button"
            onClick={onGoogleLogin}
            disabled={loading || resetLoading}
          >
            <GoogleIcon />
            <span>Continue with Google</span>
          </button>

          <p className="login-google-consent-note">
            By continuing with Google, you agree to our{" "}
            <button className="login-privacy-link" type="button" onClick={onOpenPrivacy}>
              Privacy Policy
            </button>{" "}
            and consent to learning-data processing.
          </p>

          <div className="login-divider"><span>or continue with email</span></div>

          <form className="login-form" onSubmit={onSubmit}>
            <div className="login-field">
              <label htmlFor="login-email">Email address</label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(event) => onEmailChange(event.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </div>

            {isSignup && (
              <div className="login-field">
                <label htmlFor="login-confirm-email">Confirm email address</label>
                <input
                  id="login-confirm-email"
                  type="email"
                  value={confirmEmail}
                  onChange={(event) => onConfirmEmailChange(event.target.value)}
                  placeholder="Enter your email again"
                  autoComplete="email"
                  required
                />
              </div>
            )}

            <div className="login-field">
              <div className="login-field-label-row">
                <label htmlFor="login-password">Password</label>
                {!isSignup && (
                  <button
                    className="login-forgot-button"
                    type="button"
                    onClick={onForgotPassword}
                    disabled={loading || resetLoading}
                  >
                    {resetLoading ? "Sending..." : "Forgot password?"}
                  </button>
                )}
              </div>
              <div className="login-password-shell">
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => onPasswordChange(event.target.value)}
                  placeholder={isSignup ? "At least 6 characters" : "Enter your password"}
                  autoComplete={isSignup ? "new-password" : "current-password"}
                  minLength={6}
                  required
                />
                <button
                  className="login-password-toggle"
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword((current) => !current)}
                >
                  <EyeIcon open={showPassword} />
                </button>
              </div>
            </div>

            {isSignup && (
              <div className="login-consent-row">
                <input
                  id="login-privacy-consent"
                  type="checkbox"
                  checked={privacyConsent}
                  onChange={(event) => onPrivacyConsentChange(event.target.checked)}
                />
                <div>
                  <label htmlFor="login-privacy-consent">
                    I agree to the
                  </label>{" "}
                  <button className="login-privacy-link" type="button" onClick={onOpenPrivacy}>
                    Privacy Policy
                  </button>{" "}
                  <span>and consent to the account and learning-data processing described there.</span>
                </div>
              </div>
            )}

            {error && <div className="login-message error" role="alert">{error}</div>}
            {notice && <div className="login-message success" role="status">{notice}</div>}

            <button className="login-submit-button" type="submit" disabled={loading || resetLoading}>
              <span>{loading ? "Please wait..." : submitLabel}</span>
              {!loading && (
                <span className="login-submit-arrow" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                </span>
              )}
            </button>
          </form>

          <div className="login-account-switch">
            {allowSignup ? (
              isSignup ? (
                <p>Already have an account? <button type="button" onClick={() => onModeChange("login")}>Log in</button></p>
              ) : (
                <p>New to InkSolver? <button type="button" onClick={() => onModeChange("signup")}>Create an account</button></p>
              )
            ) : (
              <p>Use your regular InkSolver account to continue securely.</p>
            )}
            <button className="login-footer-privacy" type="button" onClick={onOpenPrivacy}>
              Privacy Policy
            </button>
          </div>
        </div>

        <p className="login-auth-footnote">Your progress stays connected to your account.</p>
      </section>
    </main>
  );
}
